import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getPortfolioItems } from '../lib/api'
import GlassCard from '../components/ui/GlassCard'
import ScrollReveal from '../components/ui/ScrollReveal'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import Parallax from '../components/ui/Parallax'
import type { PortfolioItem } from '../types'

const categories = [
  { value: 'all', label: 'All Projects' },
  { value: 'web-design', label: 'Web Design' },
  { value: 'development', label: 'Development' },
  { value: 'ecommerce', label: 'eCommerce' },
  { value: 'seo', label: 'SEO' },
  { value: 'branding', label: 'Branding' },
] as const

export default function Portfolio() {
  const [data, setData] = useState<PortfolioItem[]>([])
  const [filter, setFilter] = useState<string>('all')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    getPortfolioItems(filter as PortfolioItem['category'] | 'all').then(
      (res) => {
        setData(res)
        setLoading(false)
      }
    )
  }, [filter])

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gray-950" />
        <Parallax speed={0.15} className="absolute top-0 right-0 w-[500px] h-[500px]">
          <div className="w-full h-full rounded-full bg-electric-blue/10 blur-[120px]" />
        </Parallax>
        <Parallax speed={0.12} className="absolute bottom-0 left-0 w-[400px] h-[400px]">
          <div className="w-full h-full rounded-full bg-electric-purple/10 blur-[120px]" />
        </Parallax>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-4 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue bg-electric-blue/10 rounded-full border border-electric-blue/20"
          >
            Our Work
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white"
          >
            Our{' '}
            <span className="bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent">
              Portfolio
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-lg text-gray-400 max-w-xl mx-auto"
          >
            Explore our latest projects showcasing cutting-edge design and
            development.
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setFilter(cat.value)}
                className={`px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 ${
                  filter === cat.value
                    ? 'bg-gradient-to-r from-electric-blue to-electric-purple text-white shadow-lg shadow-electric-blue/20'
                    : 'text-gray-400 bg-white/5 border border-white/5 hover:border-white/20 hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Project Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <LoadingSpinner text="Loading projects..." />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={filter}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {data.map((item, i) => (
                  <ScrollReveal key={item.id} delay={i * 0.05}>
                    <GlassCard className="group relative overflow-hidden h-full flex flex-col">
                      <div className="relative aspect-[4/3] shrink-0 overflow-hidden">
                        <img
                          src={item.thumbnail}
                          alt={item.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/20 to-transparent" />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 text-xs font-medium text-white bg-electric-blue/80 rounded-full backdrop-blur-sm capitalize">
                            {item.category.replace('-', ' ')}
                          </span>
                        </div>
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute inset-0 flex items-center justify-center bg-gray-950/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          >
                            <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-electric-blue to-electric-purple shadow-lg shadow-electric-blue/30 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              View Project
                            </span>
                          </a>
                        )}
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        {item.url ? (
                          <a href={item.url} target="_blank" rel="noopener noreferrer" className="group/title">
                            <h3 className="text-lg font-bold text-white group-hover/title:text-electric-blue transition-colors duration-300">
                              {item.title}
                            </h3>
                          </a>
                        ) : (
                          <h3 className="text-lg font-bold text-white">
                            {item.title}
                          </h3>
                        )}
                        <p className="mt-2 text-sm text-gray-400 line-clamp-2">
                          {item.description}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-1.5 flex-1 content-start">
                          {item.technologies.map((tech) => (
                            <span
                              key={tech}
                              className="px-2.5 py-1 text-xs font-medium text-gray-500 bg-white/5 rounded-md"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-sm shrink-0">
                          <span className="text-gray-500">{item.client}</span>
                          {item.url && (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-full bg-gradient-to-r from-electric-blue to-electric-purple hover:shadow-lg hover:shadow-electric-blue/25 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
                            >
                              View Demo
                              <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>
                    </GlassCard>
                  </ScrollReveal>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </section>
    </main>
  )
}
